'use client';

import { type ComponentPropsWithRef } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '@/lib/utils';
import { Badge as BadgeAtom } from '@/shared-ui/atoms/badge';
import { Button as ButtonAtom, type ButtonProps } from '@/shared-ui/atoms/button';
import { CopyIcon } from '@/shared-ui/icons/copy';
import { MatchesProgress as MatchesProgressMolecule } from '@/shared-ui/molecules/matches-progress';
import { Typography } from '@/shared-ui/atoms/typography';

type RootProps = ComponentPropsWithRef<'div'> & {
    asChild?: boolean;
    rounded?: boolean;
};

const Root = ({ asChild = false, rounded = false, className, ref, children, ...props }: RootProps) => {
    const Comp = asChild ? Slot : 'div';

    return (
        <Comp
            ref={ref}
            data-slot="profile-card"
            data-rounded={rounded ? 'true' : 'false'}
            className={cn(
                'group/profile-card w-full bg-background text-foreground data-[rounded=true]:rounded-md',
                className
            )}
            {...props}>
            {children}
        </Comp>
    );
};
Root.displayName = 'ProfileCard.Root';

type BodyProps = ComponentPropsWithRef<'div'> & {
    noTopPadding?: boolean;
};

type HeadingProps = ComponentPropsWithRef<'div'>;

const Heading = ({ className, ref, ...props }: HeadingProps) => (
    <div className={cn(className)} ref={ref} data-slot="profile-card-heading" {...props} />
);
Heading.displayName = 'ProfileCard.Heading';

const Body = ({ className, ref, noTopPadding = false, ...props }: BodyProps) => (
    <div
        ref={ref}
        data-slot="profile-card-body"
        className={cn('flex items-stretch gap-4 p-4', noTopPadding && 'pt-0', className)}
        {...props}
    />
);
Body.displayName = 'ProfileCard.Body';

type ImageProps = Omit<ComponentPropsWithRef<'img'>, 'alt'> & {
    alt: string;
    stretch?: boolean;
};

const Image = ({ className, ref, alt, stretch = false, src, ...props }: ImageProps) => {
    if (!src) {
        return null;
    }

    return (
        <img
            ref={ref}
            data-slot="profile-card-image"
            src={src}
            alt={alt}
            className={cn(
                'shrink-0 object-cover',
                stretch ? 'h-auto w-[112px] self-stretch -my-4 -ml-4 rounded-none' : 'h-[96px] w-[96px] rounded-sm',
                stretch && 'group-data-[rounded=true]/profile-card:rounded-l-md',
                className
            )}
            loading="lazy"
            decoding="async"
            {...props}
        />
    );
};
Image.displayName = 'ProfileCard.Image';

type ContentProps = ComponentPropsWithRef<'div'>;

const Content = ({ className, ref, ...props }: ContentProps) => (
    <div
        ref={ref}
        data-slot="profile-card-content"
        className={cn('flex min-w-0 flex-1 flex-col items-start gap-3', className)}
        {...props}
    />
);
Content.displayName = 'ProfileCard.Content';

type BadgeType = ComponentPropsWithRef<typeof BadgeAtom>['type'];

type BadgeProps = ComponentPropsWithRef<'div'> & {
    copyable?: boolean;
    copyText?: string;
    copyAriaLabel?: string;
    onCopy?: (text: string) => void;
    badgeType?: BadgeType;
};

const Badge = ({
    className,
    copyable = false,
    copyText,
    copyAriaLabel = 'Copy badge text',
    onCopy,
    badgeType = 'gwp',
    children,
    ref,
    ...props
}: BadgeProps) => {
    if (children == null) {
        return null;
    }
    if (typeof children === 'string' && children.trim().length === 0) {
        return null;
    }

    const resolvedCopyText = copyText ?? (typeof children === 'string' ? children : '');

    const handleCopy = async () => {
        if (!resolvedCopyText || typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
            return;
        }
        await navigator.clipboard.writeText(resolvedCopyText);
        onCopy?.(resolvedCopyText);
    };

    return (
        <div
            ref={ref}
            data-slot="profile-card-badge-row"
            className="flex w-full items-start justify-between gap-2"
            {...props}>
            <BadgeAtom
                data-slot="profile-card-badge"
                type={badgeType}
                className={cn('rounded-md px-3 py-2 text-sm leading-none tracking-[0.01em]', className)}>
                {children}
            </BadgeAtom>
            {copyable ? (
                <button
                    type="button"
                    data-slot="profile-card-copy-button"
                    aria-label={copyAriaLabel}
                    onClick={() => void handleCopy()}
                    className="inline-flex size-8 cursor-pointer justify-end rounded-full text-foreground outline-none transition-opacity hover:cursor-pointer hover:opacity-70 focus-visible:ring-2 focus-visible:ring-current/40">
                    <CopyIcon className="size-4" />
                </button>
            ) : null}
        </div>
    );
};
Badge.displayName = 'ProfileCard.Badge';

type MatchesProgressProps = ComponentPropsWithRef<'div'> & {
    value: number;
    label?: string;
    tooltip?: string;
};

const MatchesProgress = ({ className, value, label, tooltip, ref, ...props }: MatchesProgressProps) => (
    <MatchesProgressMolecule.Root
        ref={ref}
        data-slot="profile-card-matches-progress"
        className={cn(className)}
        {...props}>
        <MatchesProgressMolecule.Progress value={value} />
        <MatchesProgressMolecule.Text>{label ?? `${value}%`}</MatchesProgressMolecule.Text>
        {tooltip ? <MatchesProgressMolecule.Icon>{tooltip}</MatchesProgressMolecule.Icon> : null}
    </MatchesProgressMolecule.Root>
);
MatchesProgress.displayName = 'ProfileCard.MatchesProgress';

type CopyProps = ComponentPropsWithRef<'p'>;

const Copy = ({ className, ref, children, ...props }: CopyProps) => {
    if (children == null) {
        return null;
    }

    return (
        <Typography className={className} ref={ref} data-slot="profile-card-copy" variant="p" weight="light" {...props}>
            {children}
        </Typography>
    );
};
Copy.displayName = 'ProfileCard.Copy';

type ButtonAtomProps = Omit<ButtonProps, 'variant' | 'price' | 'originalPrice'>;

const Button = ({ className, children, ...props }: ButtonAtomProps) => {
    if (children == null) {
        return null;
    }

    return (
        <ButtonAtom
            data-slot="profile-card-button"
            variant="tertiary"
            className={cn('h-auto self-start cursor-pointer py-0 hover:cursor-pointer', className)}
            {...props}>
            {children}
        </ButtonAtom>
    );
};
Button.displayName = 'ProfileCard.Button';

const ProfileCard = {
    Root,
    Heading,
    Body,
    Image,
    Content,
    Badge,
    MatchesProgress,
    Copy,
    Button,
} as const;

export { ProfileCard };
